<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';

const props = defineProps({
  modelValue: { type: String, default: '' },
  // The node that was selected when this uploader was shown. Captured before
  // the request so a later selection cannot receive the finished URL.
  nodeId: { type: [Number, String], default: null },
});

const emit = defineEmits(['update:modelValue', 'uploaded']);

const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
];

const { t } = useI18n();
const globalConfig = useMapGetter('globalConfig/get');

const fileInput = ref(null);
const isUploading = ref(false);
const previewFailed = ref(false);

const maxSizeMb = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);
const hasImage = computed(() => Boolean(props.modelValue));

watch(
  () => props.modelValue,
  () => {
    previewFailed.value = false;
  }
);

const isAcceptedImage = file => {
  if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return true;
  if (file.type) return false;
  return /\.(png|jpe?g|gif|webp)$/i.test(file.name || '');
};

const openPicker = () => {
  if (isUploading.value) return;
  fileInput.value?.click();
};

const onFileChange = async event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  if (!isAcceptedImage(file)) {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.TYPE_ERROR'));
    return;
  }

  if (!checkFileSizeLimit(file, maxSizeMb.value)) {
    useAlert(
      t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.SIZE_ERROR', {
        size: maxSizeMb.value,
      })
    );
    return;
  }

  const targetNodeId = props.nodeId;
  isUploading.value = true;
  try {
    const { fileUrl } = await uploadFile(file);
    if (!fileUrl) {
      useAlert(t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.ERROR'));
      return;
    }
    emit('uploaded', { nodeId: targetNodeId, url: fileUrl });
  } catch {
    useAlert(t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const removeImage = () => emit('update:modelValue', '');

const onUrlChange = value => emit('update:modelValue', value);
</script>

<template>
  <div class="flex flex-col min-w-0 gap-1">
    <span class="mb-0.5 text-heading-3 text-n-slate-12">
      {{ t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.LABEL') }}
    </span>
    <input
      ref="fileInput"
      type="file"
      class="hidden"
      accept="image/png,image/jpeg,image/gif,image/webp"
      @change="onFileChange"
    />

    <div
      v-if="hasImage"
      class="flex flex-col gap-2 overflow-hidden border rounded-lg border-n-weak bg-n-alpha-1"
    >
      <img
        v-if="!previewFailed"
        :src="modelValue"
        :alt="t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.PREVIEW_ALT')"
        class="object-contain w-full max-h-40 bg-n-alpha-2"
        @error="previewFailed = true"
      />
      <div
        v-else
        class="flex items-center justify-center h-24 text-n-slate-10 bg-n-alpha-2"
      >
        <Icon icon="i-lucide-image-off" class="size-5" />
      </div>
      <div class="flex flex-wrap items-center gap-2 px-2 pb-2">
        <Button
          size="xs"
          variant="faded"
          color="slate"
          icon="i-lucide-upload"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.REPLACE')"
          :is-loading="isUploading"
          :disabled="isUploading"
          @click="openPicker"
        />
        <Button
          size="xs"
          variant="ghost"
          color="ruby"
          icon="i-lucide-trash-2"
          :label="t('AGENT_BOTS.VISUAL_BUILDER.REMOVE')"
          :disabled="isUploading"
          @click="removeImage"
        />
      </div>
    </div>

    <button
      v-else
      type="button"
      class="flex flex-col items-center justify-center gap-1.5 px-3 py-6 text-center border border-dashed rounded-lg border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-60"
      :disabled="isUploading"
      @click="openPicker"
    >
      <Icon icon="i-lucide-image-plus" class="size-4" />
      <span class="text-xs">
        {{
          isUploading
            ? t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.UPLOADING')
            : t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.PROMPT')
        }}
      </span>
    </button>

    <p class="mt-2 mb-0 text-xs text-center text-n-slate-10">
      {{ t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.OR') }}
    </p>
    <Input
      :model-value="modelValue"
      size="sm"
      :label="t('AGENT_BOTS.VISUAL_BUILDER.FIELDS.IMAGE_URL')"
      :placeholder="t('AGENT_BOTS.VISUAL_BUILDER.IMAGE_UPLOAD.URL_PLACEHOLDER')"
      :disabled="isUploading"
      @update:model-value="onUrlChange"
    />
  </div>
</template>
